import { createStyles, Flex, Grid, Group, Tooltip } from '@mantine/core';
import type { IconNames } from '@repo/foundations';
import { Icon, Text } from '@repo/foundations';

import type { ColorNames } from '@repo/theme/utils';
import dayjs from 'dayjs';
import { formatShortTimeDifference } from '../../../utils/time';

export interface ActivityItem {
	iconName: IconNames;
	iconColor?: ColorNames;
	timestamp: string;
	message: string;
	iconFillColor?: ColorNames;
	order?: number;
}

interface AutomationJobActivityItemProps {
	activityItem: ActivityItem;
	showLine?: boolean;
}

const useStyles = createStyles((theme) => ({
	icon: {
		maxWidth: theme.other.iconSize.md + theme.other.space[2] * 2,
		display: 'flex',
	},
	content: {
		paddingLeft: 0,
		paddingX: theme.other.space[2],
	},
	line: {
		paddingTop: theme.spacing.sm,
		width: theme.other.space[0.25],
		height: `calc(100% - ${theme.other.space[7]}px)`,
		minHeight: theme.other.space[5],
		backgroundColor: theme.other.getColor('border/secondary/default'),
	},
}));

export function AutomationJobActivityItem({
	activityItem,
	showLine = false,
}: AutomationJobActivityItemProps) {
	const formattedTime = `${formatShortTimeDifference(activityItem.timestamp)}`;
	const { classes } = useStyles();

	return (
		<Grid pt={0}>
			<Grid.Col span={2} className={classes.icon}>
				<Flex direction="column" align="center" gap="3xs">
					<Icon
						name={activityItem.iconName}
						color={activityItem.iconColor}
						fillColor={activityItem.iconFillColor}
					/>
					{showLine && <div className={classes.line} />}
				</Flex>
			</Grid.Col>
			<Grid.Col span="auto" className={classes.content}>
				<Group spacing="xs" noWrap>
					<Text size="sm" color="text/secondary/default">
						{activityItem.message}
					</Text>
					<Tooltip label={dayjs(activityItem.timestamp).format('LLL')}>
						<Text size="xs" color="text/secondary/default" truncate>
							{formattedTime}
						</Text>
					</Tooltip>
				</Group>
			</Grid.Col>
		</Grid>
	);
}
